import React, { useMemo, useState } from "react";
import {
  Button,
  Card,
  Col,
  Container,
  FormControl,
  Row,
} from "react-bootstrap";
import { PersonCircle } from "react-bootstrap-icons";
import { toast } from "react-toastify";
import HorizontalProgress from "../../HorizontalProgress";
import { makeRESTApiRequests } from "../../helpers/api";
import {
  ENDPOINTS,
  PROFILE_FIELDS_TO_EDIT,
  PROFILE_FIELDS_TO_SHOW,
  PROFILE_PASSWORD_FIELDS,
} from "../../helpers/constants";
import useAuth from "../../hooks/useAuth";
import Heading from "../Heading";
import OverViewColumns from "../OverViewColumns";

const Index = () => {
  const { user, onUserChange } = useAuth();
  const [editMode, setEditMode] = useState(false);
  const [submittingProfile, setSubmittingProfile] = useState(false);
  const [changePasswordData, setChangePasswordData] = useState({});
  const [editingPasswordMeta, setEditingProfileMeta] = useState(null);

  const onSaveProfileClick = async () => {
    const emptyField = PROFILE_FIELDS_TO_EDIT.find(
      (field) => !editingPasswordMeta[field?.key]
    );

    if (emptyField) {
      return toast.error(`${emptyField?.label} Field can't be empty`);
    }

    setSubmittingProfile(true);
    const { response, error } = await makeRESTApiRequests({
      endpoint: ENDPOINTS.UPDATE_USER_DETAILS,
      requestBody: {
        name: editingPasswordMeta?.name,
        phone: editingPasswordMeta?.phone,
      },
    });
    if (error) {
      setSubmittingProfile(false);
      return toast.error(error);
    }

    setSubmittingProfile(false);
    setEditMode(false);
    toast.success("Profile successfully updated");
    onUserChange(response);
    setEditingProfileMeta(null);
  };

  const fieldsToShow = useMemo(() => {
    return editMode ? PROFILE_FIELDS_TO_EDIT : PROFILE_FIELDS_TO_SHOW;
  }, [editMode]);

  const onCancelProfileEdit = () => {
    setEditMode(false);
    setEditingProfileMeta(user);
  };

  const onChangePasswordFormSubmit = async () => {
    const emptyField = PROFILE_PASSWORD_FIELDS.find(
      (field) => !changePasswordData[field?.key]
    );
    if (emptyField) {
      return toast.error(`${emptyField?.label} Field can't be empty`);
    }

    if (
      changePasswordData?.newPassword !== changePasswordData?.confirmPassword
    ) {
      return toast.error("New and conform passwords do not match");
    }
    setSubmittingProfile(true);
    const { response, error } = await makeRESTApiRequests({
      endpoint: ENDPOINTS.CHANGE_PASSWORD,
      requestBody: {
        oldPassword: changePasswordData?.oldPassword,
        newPassword: changePasswordData?.newPassword,
      },
    });
    if (error) {
      setSubmittingProfile(false);
      return toast.error(error);
    }
    setSubmittingProfile(false);
    toast.success("Password changed successfully");
  };

  return (
    <>
      <Container className="px-0 px-md-5 py-3 bg-white" fluid>
        <Row>
          <Col xs={12} className="px-4 px-md-5">
            <Card className="fade-in ">
              <Card.Body className="left-bar rounded">
                <div className="d-flex">
                  <div className="d-none d-md-block px-2 py-3 text-dark">
                    <PersonCircle size={50} />
                  </div>
                  <div className="d-md-none px-1 py-3 text-dark">
                    <PersonCircle size={35} />
                  </div>
                  <div className="flex-grow-1 bg-white px-4">
                    {user && (
                      <>
                        <Heading
                          title={"Profile Details"}
                          withEditToggle
                          editMode={editMode}
                          onEditClick={() => {
                            setEditMode(true);
                            setEditingProfileMeta({ ...user });
                          }}
                          onCancelClick={onCancelProfileEdit}
                          onSaveClick={onSaveProfileClick}
                          // inProgress={savingProfile}
                        />

                        {!editMode ? (
                          <>
                            <OverViewColumns
                              md={6}
                              className="m-1 mb-4"
                              fieldsToShow={fieldsToShow}
                              data={user}
                            />
                          </>
                        ) : (
                          <Row className="m-1 mb-4">
                            {fieldsToShow.map(
                              ({ label, key, type = "text" }) => {
                                return (
                                  <Col
                                    xs={12}
                                    md={6}
                                    key={key}
                                    className="mt-2  px-2"
                                  >
                                    <h6 className="mb-1 text-dark ">
                                      <span className="text-muted mid">
                                        {label}:{" "}
                                      </span>
                                    </h6>
                                    <FormControl
                                      size="sm"
                                      className="mb-0  text-dark px-2 py-1 large rounded"
                                      type={type}
                                      value={editingPasswordMeta?.[key]}
                                      onChange={(e) =>
                                        setEditingProfileMeta({
                                          ...editingPasswordMeta,
                                          [key]: e.target.value,
                                        })
                                      }
                                    />
                                  </Col>
                                );
                              }
                            )}
                          </Row>
                        )}

                        <Heading title={"Password Change"} />

                        {/* Change Password Form */}
                        <Row className="m-1 mb-4">
                          {PROFILE_PASSWORD_FIELDS.map(
                            ({ key, label, type }) => {
                              return (
                                <Col
                                  xs={12}
                                  md={6}
                                  key={key}
                                  className="mt-2  px-2"
                                >
                                  <h6 className="mb-1 text-dark ">
                                    <span className="text-muted mid">
                                      {label}:{" "}
                                    </span>
                                  </h6>
                                  <FormControl
                                    size="sm"
                                    className="mb-0  text-dark px-2 py-1 large rounded"
                                    type={type}
                                    value={changePasswordData[key] || ""}
                                    onChange={(e) =>
                                      setChangePasswordData({
                                        ...changePasswordData,
                                        [key]: e.target.value,
                                      })
                                    }
                                  />
                                </Col>
                              );
                            }
                          )}
                        </Row>
                      </>
                    )}
                  </div>
                </div>

                {/* Save Button */}
                <div className="d-flex justify-content-end align-items-center my-2">
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={onChangePasswordFormSubmit}
                    disabled={submittingProfile}
                  >
                    Change Password
                  </Button>
                </div>

                {submittingProfile && (
                  <HorizontalProgress text={"Updating Profile"} />
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </>
  );
};

export default Index;
